import "@styles/components/modal.css";
import ajax from "@common/ajax";
import { on, off, fire } from "delegated-events";
import A11yDialog from "a11y-dialog";
import { OverlayScrollbars } from 'overlayscrollbars';
import qs from 'qs';
import { handleModalCheckedClass } from '@common/util';
import 'overlayscrollbars/overlayscrollbars.css';
import '@styles/components/scrollbar.css';
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";
import "@styles/components/tooltip.css";
import dropdown from "./dropdown";
import TomSelect from "tom-select";
import toggle from "./toggle";
import categoryAccordion from "./category-accordion";

const generateTemplate = (id, useDefaultClose = true, useCustomClose = false, title = false, fullModal = false) => {
  const role = useDefaultClose ? "dialog" : "alertdialog";
  const labeledBy = title ? 'aria-labelledby="modal-title"' : "";
  const modalType = fullModal ? "full-modal" : "half-modal";

  const html = `
    <div id="${id}" aria-hidden="true" class="modal-wrapper" ${labeledBy} role="${role}">
      <div class="modal-overlay ${modalType}" ${useDefaultClose && "data-a11y-dialog-hide"}></div>

      ${
        useCustomClose === false
          ? '<button type="button" class="modal-close" data-a11y-dialog-hide aria-label="Close dialog"> <svg width="26" height="26" viewBox="0 0 26 26" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M0.26988 25.7277C0.632041 26.0904 1.22055 26.091 1.58329 25.7283C1.58329 25.7283 1.58329 25.7283 1.58387 25.7277L12.9966 14.316L24.4139 25.7277C24.7767 26.0904 25.3652 26.0904 25.7279 25.7277C26.0907 25.365 26.0907 24.7765 25.7279 24.4138L14.3106 13.0022L25.7233 1.5859C26.086 1.22319 26.086 0.634737 25.7233 0.27203C25.3606 -0.0906768 24.772 -0.0906768 24.4093 0.27203L12.9966 11.6883L1.57923 0.276673C1.18979 -0.0570175 0.603602 -0.0117517 0.26988 0.377651C-0.0278586 0.725269 -0.0278586 1.23828 0.26988 1.5859L11.6826 13.0022L0.26988 24.4185C-0.0899599 24.7806 -0.0899599 25.3656 0.26988 25.7277Z" fill="currentColor"/></svg> </button>'
          : ""
      }

      <div class="modal-container ${title}">
        <svg class="site-loader site-loader--centered site-loader--light" viewBox="0 0 50 50" aria-hidden="true">
          <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
        </svg>
      </div>
    </div>
  `;

  return html;
};

const populateModal = async (opts = {}, modalEl) => {
  const type = opts.type;
  const title = opts.title ?? "";
  const url = opts.url;

  let modalContent = "";

  if (type === "video") {
    modalContent = `
      <div class="mx-auto w-full max-w-5xl modal-content" role="document">
        <h1 id="modal-title" class="sr-only">Watch ${title}</h1>
        <div class="relative aspect-video modal-body">
          <iframe id="Video" title="Watch ${type}" style="border: none" src="${url}?autoplay=1&rel=0&enablejsapi=1" allowfullscreen class="absolute inset-0 w-full h-full"></iframe>
        </div>
      </div>
    `;

    modalEl.querySelector(".modal-container").innerHTML = modalContent;
  }

  if (type === "full-page") {
    modalContent = await ajax.get(url).text();

    modalEl.classList.add("full-page");
    modalEl.querySelector(".modal-container").classList.add("full-page");
    modalEl.querySelector(".modal-container").innerHTML = modalContent;

    // Add Custom Scrollbar
    const scrollbar = document.querySelector('[data-scrollbar]');

    if (scrollbar) {
        const osInstance = OverlayScrollbars(scrollbar, {
            scrollbars: {
                theme: 'os-theme-custom',
            },
        });
    }

    // Only initialize TomSelect for the modal's county field
    const countyInput = document.getElementById('county-modal');
    const dataElement = document.getElementById('counties-modal');
    if (countyInput && dataElement && !countyInput.tomselect) {
        const dataJSON = dataElement.getAttribute('data-json');
        const data = JSON.parse(dataJSON.replace(/&quot;/g, '"').replace(/&#039;/g, "'"));
        const options = Object.keys(data).map(key => ({ value: key, text: data[key] }));

        // Get county param from URL (support array or comma-separated string)
        const currentParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });
        let selectedItemsArray = [];
        if (currentParams.county) {
            if (Array.isArray(currentParams.county)) {
                selectedItemsArray = currentParams.county;
            } else if (typeof currentParams.county === 'string') {
                selectedItemsArray = currentParams.county.split(',');
            }
        } else {
            // fallback to data-selected-items attribute if present
            const selectedItems = dataElement.getAttribute('data-selected-items');
            selectedItemsArray = selectedItems ? selectedItems.split(',') : [];
        }

        const updateCountyParam = (items) => {
            const params = qs.parse(window.location.search, { ignoreQueryPrefix: true });
            if (items && items.length > 0) {
                params.county = items.join(',');
            } else {
                delete params.county;
            }
            const newQuery = qs.stringify(params, { addQueryPrefix: true, arrayFormat: 'comma' });
            window.history.replaceState({}, '', window.location.pathname + newQuery);
        };

        const tomselectInstance = new TomSelect(countyInput, {
            maxItems: null,
            maxOptions: null,
            options: options,
            selectOnTab: true,
            create: false,
            items: selectedItemsArray,
            wrapperClass: 'ts-wrapper search-county-modal',
            plugins: {
                'clear_button':{
                    'title':'Remove all selected options',
                    'html':function(data) {
                        return `<div class="${data.className}" title="${data.title}"><svg width="15" height="15" viewBox="0 0 15 15" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M0.156 14.843a0.537 0.537 0 0 0 0.758 0l6.585 -6.584 6.587 6.584a0.537 0.537 0 0 0 0.758 -0.758L8.256 7.501l6.584 -6.586a0.537 0.537 0 0 0 -0.758 -0.758L7.498 6.743 0.911 0.16a0.536 0.536 0 0 0 -0.755 0.755l6.585 6.586 -6.585 6.588a0.537 0.537 0 0 0 0 0.755" fill="currentColor"/></svg></div>`;
                    },
                },
                'remove_button':{
                    'title':'Remove this option',
                },
                'no_active_items':{},
            },
            onItemAdd: function() {
                let tsWrapper = '.ts-wrapper.search-county-modal .ts-control';
                scrollToRight(tsWrapper);
                updateCountyParam(this.items);
            },
            onItemRemove: function() {
                updateCountyParam(this.items);
            },
            onClear: function() {
                updateCountyParam([]);
            },
        });
        // Initial sync in case of pre-populated values
        updateCountyParam(tomselectInstance.items);
        countyInput.setAttribute('aria-hidden', 'true');
    }
  }

  fire(document.documentElement, "modal:populate", { modalEl });

  handleCheckboxes();
  handleZipRadius(modalEl);

  // Handle tooltip on modal
  tippy("[data-modal-tooltip]", {
    theme: "light",
    placement: "bottom",
    content: (ref) => {
        return ref.getAttribute("aria-describedby");
    },
    allowHTML: true,
    touch: true,
    trigger: "click",
    interactive: true,
  });

  dropdown.run();
  toggle.run(); // <-- Add this line
  categoryAccordion.run();
};

const scrollToRight = (tsWrapper) => {

    let searchInputField = document.querySelector(tsWrapper);
    searchInputField.scrollLeft = searchInputField.scrollWidth;

    if (window.innerWidth <= 767) {
        //let mobileSearchInputField = document.querySelector('.mobileSearch .ts-control');
        //mobileSearchInputField.scrollLeft = mobileSearchInputField.scrollWidth;
    }

};

const handleZipRadius = (modalEl = null) => {
  const currentParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });

  // Use modal context if available, otherwise fall back to document
  const context = modalEl || document;

  if (currentParams.zip) {
      // Set the zip code input value
      const zipCodeInput = context.querySelector("#zip");
      if (zipCodeInput) {
          zipCodeInput.value = currentParams.zip;
      }
  }

  if (currentParams.radius) {
      // Set the radius input value
      const radiusInput = context.querySelector("#radius");
      if (radiusInput) {
          radiusInput.value = currentParams.radius;
      }
  }

  if (currentParams.practice) {
      // Set the practice input value (look specifically in modal context)
      console.log('tata')
      const practiceInput = context.querySelector("#practice");
      if (practiceInput) {
          practiceInput.value = currentParams.practice;
      }
  }
};

const accessibilityWidgetSelector = [
  '#userway',
  '.uwy',
  '[id^="userway"]',
  'iframe[title*="UserWay"]',
].join(', ');

let hiddenAccessibilityWidget = null;

const hideAccessibilityWidget = () => {
  const widget = document.querySelector(accessibilityWidgetSelector);

  if (widget) {
    hiddenAccessibilityWidget = widget;
    widget.style.display = 'none';
  }
};

const showAccessibilityWidget = () => {
  if (hiddenAccessibilityWidget) {
    hiddenAccessibilityWidget.style.display = '';
    hiddenAccessibilityWidget = null;
  }
};

const handleModal = (e) => {
  e.preventDefault();

  const triggerEl = e.target.closest("[data-modal]");

  openModal({
    type: triggerEl.dataset.modal,
    title: triggerEl.dataset.modalTitle,
    url: triggerEl.href,
    updateUrl: triggerEl.dataset.modalUpdateUrl ?? false,
    useDefaultClose: triggerEl.dataset.modalDisableDefaultClose === undefined,
    useCustomClose: triggerEl.dataset.modalUseCustomClose !== undefined,
    fullModal: triggerEl.dataset.modalFullModal === undefined,
  });

  return false;
};

const openModal = (opts = {}) => {
  const type = opts.type;
  const title = opts.title ?? false;
  let useCustomClose = opts.useCustomClose ?? false;
  let useDefaultClose = opts.useDefaultClose ?? true;
  let fullModal = opts.fullModal ?? false;

  if (type === "video") {
    useDefaultClose = true;
    fullModal = false;
  }

  if (type === "full-page") {
    useDefaultClose = false;
    fullModal = true;
  }

  const id = `modal-${Math.floor(Math.random() * Date.now())}`;
  const template = generateTemplate(id, useDefaultClose, useCustomClose, title, fullModal);
  const node = document.createRange().createContextualFragment(template);

  document.body.append(node);

  const modalEl = document.getElementById(id);
  const modal = new A11yDialog(modalEl);

  const hideModal = () => {
    modal.hide();
  };

  if (useCustomClose) {
    on("click", ".modal-custom-close", hideModal);
  }

  const currentUrl = opts.defaultUrl ?? window.location.href;

  modal.on("show", () => {
    document.documentElement.classList.add("has-modal-active");
    hideAccessibilityWidget();

    if (opts.updateUrl !== false) {
      window.history.replaceState({}, "", opts.updateUrl);
    }
  });

  modal.on("hide", () => {
    document.documentElement.classList.remove("has-modal-active");
    showAccessibilityWidget();

    modal.destroy();

    if (opts.updateUrl !== false) {
      window.history.replaceState({}, "", currentUrl);
    }

    if (useCustomClose) {
      off("click", ".modal-custom-close", hideModal);
    }
  });

  modal.on("destroy", (e) => {
    document.querySelector(".modal-wrapper").remove();
    e.target.remove();
  });

  modal.show();

  populateModal(
    {
      type: type,
      url: opts.url ?? false,
      updateUrl: opts.updateUrl,
      defaultUrl: opts.defaultUrl,
    },
    modalEl,
  );
};

const handleCheckboxes = () => {
    const currentParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });

    if (currentParams.service) {
        handleModalCheckedClass(currentParams.service);
    }

    if (currentParams.subcategory) {
        handleModalCheckedClass(currentParams.subcategory);
    }

    if (currentParams.relatedCategories) {
        handleModalCheckedClass(currentParams.relatedCategories);
    }

    if (currentParams.accessibility) {
        handleModalCheckedClass(currentParams.accessibility);
    }

    if (currentParams.insurance) {
        handleModalCheckedClass(currentParams.insurance);
    }

    if (currentParams.practiceAttr) {
        handleModalCheckedClass(currentParams.practiceAttr);
    }

    if (currentParams.ageGroups) {
        handleModalCheckedClass(currentParams.ageGroups);
    }

    if (currentParams.disabilitySpecializations) {
        handleModalCheckedClass(currentParams.disabilitySpecializations);
    }

    if (currentParams.culturalGroups) {
        handleModalCheckedClass(currentParams.culturalGroups);
    }
};

export default {
  can: () => true,
  run: () => {
    const clickArgs = ["click", "[data-modal]", handleModal];

    off(...clickArgs);
    on(...clickArgs);

    // If we have video modals, load the YouTube API
    if (document.querySelectorAll('[data-modal="video"]').length) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";

      const firstScriptTag = document.getElementsByTagName("script")[0];

      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
  },
  openModal,
};
